/**
 * imgUrl — image URL optimization helpers.
 *
 * Purpose: reduce mobile image payload for Lighthouse. For third-party CDNs
 * that support query-string transforms (Unsplash, Pexels), we append
 * width/quality/format params so the browser downloads a right-sized
 * WebP/AVIF instead of a full-resolution JPEG.
 *
 * SAFE: if the URL is unknown/local we return it unchanged — no visual
 * regression risk.
 */

// Unsplash: images.unsplash.com/photo-... supports ?w= &q= &auto=format &fit=crop
// Pexels:  images.pexels.com/photos/...  supports ?auto=compress&cs=tinysrgb&w=
const UNSPLASH_RE = /(^|\.)images\.unsplash\.com$/i;
const PEXELS_RE   = /(^|\.)images\.pexels\.com$/i;

/**
 * @param {string} url - original image URL
 * @param {number} width - target CSS pixel width (real device px = width * DPR)
 * @param {{ quality?: number, fit?: string }} [opts]
 * @returns {string} optimized URL (or unchanged if not a known CDN)
 */
export function optimizeImg(url, width = 600, opts = {}) {
  if (!url || typeof url !== "string") return url;
  const { quality = 75, fit = "crop" } = opts;
  try {
    const u = new URL(url);
    if (UNSPLASH_RE.test(u.hostname)) {
      // Preserve any existing photo id / path — just replace transform params.
      u.searchParams.set("w", String(width));
      u.searchParams.set("q", String(quality));
      u.searchParams.set("auto", "format");
      u.searchParams.set("fit", fit);
      return u.toString();
    }
    if (PEXELS_RE.test(u.hostname)) {
      u.searchParams.set("auto", "compress");
      u.searchParams.set("cs", "tinysrgb");
      u.searchParams.set("w", String(width));
      return u.toString();
    }
    return url;
  } catch {
    return url;
  }
}

/**
 * Build a responsive srcSet for known CDNs. Returns undefined if not supported
 * so <img srcset={undefined}/> is silently skipped.
 */
export function srcSetFor(url, widths = [320, 480, 640, 960]) {
  if (!url || typeof url !== "string") return undefined;
  try {
    const u = new URL(url);
    if (!UNSPLASH_RE.test(u.hostname) && !PEXELS_RE.test(u.hostname)) return undefined;
    return widths.map((w) => `${optimizeImg(url, w)} ${w}w`).join(", ");
  } catch {
    return undefined;
  }
}
